import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
} from '@mui/material/Autocomplete';
import { FieldHookConfig, useField } from 'formik';

import {
  AddableAutocomplete,
  Autocomplete,
  AutocompleteProps,
} from '../../../components/Autocomplete';
import { TextField } from '../../../components/TextField';

interface FormAutocompleteExtraProps {
  name: string;
  floatingLabel?: boolean;
  label?: string;
  placeholder?: string;
  transform?: (value: any) => any;
}

type FormAutocompleteProps<T, Multiple extends boolean = false> = Omit<
  AutocompleteProps<T, Multiple, false, false>,
  'renderInput'
> &
  FormAutocompleteExtraProps &
  FieldHookConfig<Multiple extends true ? T[] : T>;

export const FormAutocomplete = <T, Multiple extends boolean = false>({
  label,
  placeholder,
  floatingLabel,
  transform,
  multiple,
  ...props
}: FormAutocompleteProps<T, Multiple>) => {
  const { name } = props;
  const [field, meta, helpers] = useField(props);
  const { setValue } = helpers;

  const fieldParams = {
    label,
    floatingLabel,
    placeholder,
    name,
  };

  return (
    <Autocomplete<T, Multiple, false, false>
      {...props}
      {...field}
      multiple={multiple as Multiple}
      onChange={(e, value) => {
        setValue(transform ? transform(value) : value);
      }}
      renderInput={(params) => (
        <TextField
          {...fieldParams}
          {...params}
          error={!!(meta.touched && meta.error)}
          helperText={meta.touched && meta.error}
        />
      )}
    />
  );
};

type FormAddableAutocompleteProps<
  T extends { title: string; inputValue?: string },
  Multiple extends boolean = false
> = Omit<AutocompleteProps<T, Multiple, false, true>, 'renderInput'> &
  FormAutocompleteExtraProps &
  FieldHookConfig<Multiple extends true ? (T | string)[] : T | string> & {
    onAddItem?: (inputValue: string) => void | Promise<void>;
    onChange?: (
      value: T | null,
      reason?: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<T>
    ) => void;
  };

export const FormAddableAutocomplete = <
  T extends { title: string; inputValue?: string },
  Multiple extends boolean = false
>({
  label,
  placeholder,
  floatingLabel,
  multiple,
  onAddItem,
  onChange,
  options,
  ...props
}: FormAddableAutocompleteProps<T, Multiple>) => {
  const { name } = props;
  const [field, meta, helpers] = useField(props);
  const { setValue } = helpers;

  const fieldParams = {
    label,
    floatingLabel,
    placeholder,
    name,
  };

  const handleChange = async (
    e: any,
    value: any,
    reason: any,
    details: any
  ) => {
    if (onChange) {
      onChange(value, reason, details);
    } else {
      // Then handle the form value
      setValue(
        multiple
          ? ((value || []) as Multiple extends true
              ? (T | string)[]
              : T | string)
          : ((value || null) as Multiple extends true
              ? (T | string)[]
              : T | string)
      );
    }
  };

  const transformedValue: AutocompleteValue<T, Multiple, false, true> =
    multiple && Array.isArray(field.value)
      ? (field.value.map((v) =>
          typeof v === 'number' || typeof v === 'string'
            ? options?.find((opt) => 'id' in opt && opt.id === v) ?? null
            : v
        ) as any)
      : typeof field.value === 'number' || typeof field.value === 'string'
      ? ((options?.find((opt) => 'id' in opt && opt.id === field.value) ||
          null) as AutocompleteValue<T, Multiple, false, true>)
      : (field.value as AutocompleteValue<T, Multiple, false, true>);

  return (
    <AddableAutocomplete<T, Multiple>
      {...props}
      {...field}
      value={transformedValue}
      options={options}
      multiple={multiple as Multiple}
      onAddItem={onAddItem}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...fieldParams}
          {...params}
          error={!!(meta.touched && meta.error)}
          helperText={meta.touched && meta.error}
        />
      )}
    />
  );
};
